import { FC } from 'react';
import { useSetAtom } from 'jotai';
import {
  WellsOverviewSection as SharedWellsOverviewSection,
  type WellsOverviewConfig,
} from 'pages/documents/structuring/wells-overview-section';
import type { StagedWell } from './types-well';
import {
  fetchWellOverviewAtom,
  removeWellAtom,
  restoreWellAtom,
  wellDetailUrl,
  wellOverviewDataAtom,
  wellOverviewErrorAtom,
  wellOverviewUrl,
} from './atoms/wells';
import {
  fetchInterestOverviewAtom,
  interestOverviewUrl,
} from './atoms/interests';
import { selectedWellIndexAtom } from './atoms/layout';
import { AddWellPanel } from './add-well-panel';
import { WellEditPanel } from './well-edit-panel';

// DO Well Overview — a thin doctype wrapper over the shared section
// (pages/documents/structuring/wells-overview-section). All behavior lives in
// the shared component; this file supplies the DO wiring:
//   • the DO wells atom bundle + URL builders (remove/restore built by id);
//   • the document-scoped `selectedWellIndexAtom(documentId)` drill-in atom (NOT
//     AFE's module-level singleton);
//   • the DO staged shape (operator / property…) and none of the AFE-only rows;
//   • an extra reload of the Interest overview — removing/restoring a well
//     mutes/un-mutes its interest line(s), which live in a separate atom;
//   • a destructive-delete confirm modal (the app norm for a destructive action).

const DO_WELLS_OVERVIEW_CONFIG: WellsOverviewConfig<StagedWell> = {
  wellOverviewDataAtom,
  wellOverviewErrorAtom,
  fetchWellOverviewAtom,
  removeWellAtom,
  restoreWellAtom,
  wellOverviewUrl,
  wellDetailUrl,
  // DO's drill-in selection is document-scoped (atomFamily keyed on documentId).
  getSelectedWellIndexAtom: (documentId) => selectedWellIndexAtom(documentId),
  // DO staged shape only — no AFE-only rows (basin, tvd, shl/bhl, quarter,
  // prospect, abstract). Legal Description spans the full cell width.
  stagedOverviewFields: (staged) => [
    { label: 'UWI / API Number', value: staged.uwiApi ?? '—' },
    { label: 'County/State', value: staged.countyState ?? '—' },
    { label: 'Operator', value: staged.operator ?? '—' },
    ...(staged.propertyName
      ? [{ label: 'Property Name', value: staged.propertyName }]
      : []),
    ...(staged.propertyNumber
      ? [{ label: 'Property Number', value: staged.propertyNumber }]
      : []),
    ...(staged.divisionOrderNumber
      ? [{ label: 'Division Order Number', value: staged.divisionOrderNumber }]
      : []),
    ...(staged.legalDescription
      ? [
          {
            label: 'Legal Description',
            value: staged.legalDescription,
            fullWidth: true,
          },
        ]
      : []),
  ],
  // Removing/restoring a well mutes/un-mutes its interest line(s) in the Interest
  // Review section (they read 'well_removed'), which lives in a SEPARATE
  // module-level atom. Refresh it alongside the well overview so the mute appears
  // without a manual page reload.
  useExtraReload: (documentId) => {
    const reloadInterests = useSetAtom(fetchInterestOverviewAtom);
    return () => reloadInterests(interestOverviewUrl(documentId));
  },
  // Delete is destructive → confirm first (soft-delete makes it recoverable, but
  // confirmation is the app norm; mirrors the Interest section's modal).
  confirmRemoval: {
    heading: 'Delete Well',
    primaryButtonText: 'Delete',
    message: (wellName) =>
      `Are you sure you want to delete ${wellName}? You can undo this from the removed well’s Undo action.`,
  },
  AddWellPanel,
  WellEditPanel,
};

interface Props {
  documentId: number | string;
}

export const WellsOverviewSection: FC<Props> = ({ documentId }) => (
  <SharedWellsOverviewSection<StagedWell>
    documentId={documentId}
    config={DO_WELLS_OVERVIEW_CONFIG}
  />
);
